import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useApolloClient } from '@apollo/client/react';
import JsCookie from 'js-cookie';
import logger from 'logger';
import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import MenuComponent from 'src/components/Menu';
import { CatalogsQuery } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import 'src/utils/carts';
import {
  CART_ID_COOKIE_KEY,
  CART_ID_QUERY_PARAM_KEY,
  DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
} from 'src/utils/constants';
import useAnalytics from 'src/analytics/useAnalytics';
import { DiscountCode, postProspect } from '../../services/rails-service';
import ConfigContext from '../../context/config';
import { DiscountProvider } from '../../context/discount';
import UserContext from '../../context/user';
import useNonNullContext from '../../hooks/useNonNullContext';
import { FunnelCart } from 'src/ts/types/cart';
import {
  getInitializedCart,
  getSellableZip,
  validateZip,
} from 'src/components/Menu/_utils/get-sellable-cart';
import { getSelectedPlan } from 'src/components/Menu/_utils/get-selected-plan';
import { Plan } from 'src/services/cart-order/plans-service';
import { ParsedUrlQuery } from 'querystring';
import { initializeMenusAndFilters } from 'src/utils/filters/initialize-menus';
import {
  funnelCartParamsToFilters,
  planToFilters,
  tenantConfigToFilters,
  urlParamsToFilters,
} from 'src/utils/filters/preselected-filters-parsers';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import { applyFilters } from 'src/utils/filters/apply-filters';
import {
  sortCatalogItems,
  sortCatalogItemsByDefault,
  Sorter,
} from 'src/utils/filters/sorters';
import { parseAndReturnSystemParams } from '../../utils/queryParams';
import { menuSorters } from '../../components/Menu/_utils/sorters';
import { setSellableCartPreferences } from '../../components/Menu/_utils/set-sellable-cart';
import {
  getUnverifiedCartDiscountCode,
  getValidatedDiscount,
} from 'src/components/Menu/_utils/discounts/get-discount';
import { getDiscountCodeClientSide } from 'src/services/rails-service';
import { UPDATE_CART } from 'src/graphql-operations/cart-order-service/carts.mutation';
import {
  UpdateCartMutation,
  UpdateCartMutationVariables,
} from 'src/ts/@types/generated/graphql/cart-order-service/graphql';
import { ClientNames } from 'src/utils/apolloClient';
import { getRequestContext } from '../../../server/utils';
import { panaceaClient } from '../../services/panacea/client';
import { Noticeable } from '@honeybadger-io/core/build/src/types';

type Props = {
  featuredSkuGroups: string[][];
};

const Menu: NextPage<Props> = (props) => {
  const router = useRouter();
  const userContext = useContext(UserContext);
  const tenantConfig = useNonNullContext(ConfigContext);
  const apolloClient = useApolloClient();
  const queryParams = router.query;
  const { identify, track } = useAnalytics();

  const [userSuppliedZipCode, setUserSuppliedZipCode] = useState<
    string | undefined
  >(undefined);
  const [userSuppliedEmail, setUserSuppliedEmail] = useState<
    string | undefined
  >(undefined);
  const [userSuppliedName, setUserSuppliedName] = useState<string | undefined>(
    undefined,
  );
  const [isZipCodeModalOpen, setIsZipCodeModalOpen] = useState(false);
  const [isInvalidZipCode, setIsInvalidZipCode] = useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [cart, setCart] = React.useState<FunnelCart | null>(null);
  const [catalogs, setCatalogs] = React.useState<CatalogsQuery['catalogs']>([]);
  const [displayCatalogs, setDisplayCatalogs] = React.useState<
    CatalogsQuery['catalogs']
  >([]);
  const [selectedFilters, setSelectedFilters] = useState<DisplayFilters>({});
  const [displayFilters, setDisplayFilters] = useState<DisplayFilters>({});
  // Put in "popular" as default sorter value
  const [selectedSorter, setSelectedSorter] = useState<Sorter>(menuSorters[0]);
  const [discount, setDiscount] = useState<DiscountCode | undefined>(undefined);
  const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>(undefined);

  // Initial Load useEffect
  useEffect(() => {
    loadCartAndCatalogs().then(() => setIsLoading(false));
  }, []);

  // When the user submits a zipcode via the modal, this useEffect fires. It's not in-line with the
  // submission function since the loadCartAndCatalogs() function relies on the state value
  // userSuppliedZipCode which may not be set when loadCartAndCatalogs() is called. This useEffect
  // ensures the zip value is set before attempting to load catalogs against it.
  useEffect(() => {
    const updateWithUserSuppliedZipCode = async () => {
      await loadCartAndCatalogs();
      setIsLoading(false);
      setIsZipCodeModalOpen(false);
    };

    if (userSuppliedZipCode) {
      updateWithUserSuppliedZipCode();
    }
  }, [userSuppliedZipCode]);

  /**
   * Whenever there's an update to the cart, we need to see if a discount needs to be pulled. If there isn't any
   * the called getValidatedDiscount() function will just return undefined.
   */
  useEffect(() => {
    const getDiscount = async () => {
      const { discountCode: queryParamDiscountCode } =
        parseAndReturnSystemParams(queryParams);

      // If no cart exists but we have a query param discount code, fetch its details
      // so the ZipCodeModal can display the publicDescription
      if (!cart && queryParamDiscountCode && userContext?.tenantId) {
        if (queryParamDiscountCode === discount?.code) return;

        const discountCode = await getDiscountCodeClientSide({
          discountCode: queryParamDiscountCode.trim().toUpperCase(),
          tenantId: userContext.tenantId,
        });
        setDiscount(discountCode);
        return;
      }

      if (!cart) return;

      // This check is to just make sure we're not re-pulling the same discount code over and over every time
      // the cart itself is updated.
      const unverfiedDiscountCode = getUnverifiedCartDiscountCode(cart);
      if (unverfiedDiscountCode === discount?.code) return;

      const verifiedDiscountCode = await getValidatedDiscount({
        cart,
        userContext,
      });

      setDiscount(verifiedDiscountCode);
    };

    getDiscount();
  }, [cart, queryParams]);

  /**
   * This is just a helper method that wraps the load promises in a try...catch. If they fail, it
   * redirects to the get-started page.
   */
  const tryPromiseWithRedirect = async <T,>(
    promise: Promise<T>,
    // @ts-expect-error Technically, this promise can return undefined in the case of an error, but the redirect means the function will not ever return.
  ): Promise<T> => {
    try {
      const result = await promise;
      return result;
    } catch (error) {
      logger.error({
        msg: 'Failed execute',
        extra: {
          context: {
            error,
          },
        },
      });

      await redirectToGetStarted(true);
    }
  };

  const redirectToGetStarted = async (clearCookie?: boolean) => {
    if (clearCookie) {
      JsCookie.remove(CART_ID_COOKIE_KEY);
    }
    await router.push({
      pathname: '/get-started',
      query: { fetchFail: true },
    });
  };

  const loadCartAndCatalogs = async () => {
    if (!userContext?.tenantId) {
      logger.error({
        msg: 'Unable to query for catalogs. `UserContext#tenantId` is undefined.',
        extra: {
          context: {
            userContext: userContext,
            queryParams: router.query,
          },
        },
      });
      return await router.push({
        pathname: '/get-started',
        query: { fetchFail: true },
      });
    }

    const zipCode = await getSellableZip({
      apolloClient,
      queryParams,
      cartCookieUuid: JsCookie.get(CART_ID_COOKIE_KEY),
      userSuppliedZipCode,
    });

    // If you can't determine a zipcode, that means you must open the modal to get that data
    if (!zipCode) {
      // Open the modal
      setIsZipCodeModalOpen(true);
      setIsLoading(false);

      // Track modal shown event
      track('Menu modal shown', { modal_variant: getModalVariant() });

      // Load some dummy catalogs in the background
      await getCatalogsAndFilters({
        tenantId: userContext.tenantId,
        zipCode: tenantConfig.defaultLoadingCatalogZipcode || '01038',
        preselectedFilterInputs: {},
      });

      return;
    }

    // Get or create a cart
    const cart = await tryPromiseWithRedirect(
      // Note about this: when creating a cart via this function it is *not* setting the preselected
      // filter funnel data. Reason being is that we don't actually have all the information we need
      // at this exact point to have filters preselected. That comes in the below getCatalogsAndFilters()
      //
      // There's a useEffect() below that actually updates the cart when the selectedFilters are
      // calculated.
      getInitializedCart({
        apolloClient,
        userContext: userContext,
        queryParams,
        tenantConfig,
        zipCode,
        cartCookieUuid: JsCookie.get(CART_ID_COOKIE_KEY),
        email: userSuppliedEmail,
        name: userSuppliedName,
      }),
    );

    // Set the context for the cookie you're working against. This value is used to reload the page
    // against the same cart should the user navigate back here.
    JsCookie.set(CART_ID_COOKIE_KEY, cart.uuid);

    // Setting the cart in the query params SHOULD NOT BE RELIED ON FOR LOGIC. This value is only being
    // put into the url for debugging, like in Full Story. This value gets set asynchronously by Next
    // and cannot be relied in to exist when you actually need it. If you want to see what cart you
    // should be working on, check the cookie.
    router.replace({
      query: { ...router.query, [CART_ID_QUERY_PARAM_KEY]: cart.uuid },
    });

    const plan = await getSelectedPlan(queryParams);
    setSelectedPlan(plan);

    await getCatalogsAndFilters({
      tenantId: userContext.tenantId,
      zipCode,
      preselectedFilterInputs: {
        funnelCart: cart,
        queryParams,
        tenantConfig,
        plan,
        featuredSkuGroups: props.featuredSkuGroups,
      },
    });

    setCart(cart);
  };

  const getCatalogsAndFilters = async (input: {
    tenantId: number;
    zipCode: string;
    preselectedFilterInputs: {
      funnelCart?: FunnelCart;
      queryParams?: ParsedUrlQuery;
      tenantConfig?: ClientSafeTenantConfig;
      plan?: Plan;
      featuredSkuGroups?: string[][];
    };
  }) => {
    const {
      tenantId,
      zipCode,
      preselectedFilterInputs: {
        funnelCart,
        queryParams: inputQueryParams,
        tenantConfig: inputTenantConfig,
        plan: inputPlan,
        featuredSkuGroups,
      },
    } = input;

    const parsedParams = parseAndReturnSystemParams(inputQueryParams);

    const tenantEligibilityLogicalId =
      funnelCart?.tenantEligibilityConfigLogicalId ||
      parsedParams.tenantEligibilityConfigLogicalId ||
      DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID;
    const {
      catalogs: fetchedCatalogs,
      displayFilters,
      selectedFilters,
    } = await initializeMenusAndFilters({
      apolloClient,
      tenantId,
      zipCode,
      tenantConfig,
      tenantEligibilityLogicalId,
      plainFiltersList: [
        inputTenantConfig ? tenantConfigToFilters(inputTenantConfig) : {},
        inputPlan ? planToFilters(inputPlan) : {},
        funnelCart
          ? funnelCartParamsToFilters(funnelCart, inputQueryParams)
          : {},
        inputQueryParams ? urlParamsToFilters(inputQueryParams) : {},
      ],
      tenantPageFilter: 'funnel',
    });

    const sortedCatalogs = sortCatalogItemsByDefault({
      catalogs: fetchedCatalogs,
      cart: funnelCart,
      featuredSkuGroups,
    });

    setCatalogs(sortedCatalogs);
    setDisplayCatalogs(sortedCatalogs);
    setSelectedFilters(selectedFilters);
    setDisplayFilters(displayFilters);
  };

  /**
   * The logic of the ZipCodeModal is pretty simple: if the zipcode that's entered is the same as the
   * current cart, close the modal. Otherwise, create a new cart with the new zip code.
   */
  const handleZipCodeModalSubmit: SubmitHandler<{
    zipCode: string;
    email?: string;
    name?: string;
  }> = async ({ zipCode, email, name }) => {
    if (!userContext) {
      logger.error({
        msg: 'Unable to submit zip code. `UserContext` is undefined.',
        extra: {
          context: {
            userContext: userContext,
            queryParams: router.query,
          },
        },
      });

      redirectToGetStarted(true);
      return;
    }

    // Identify user with Segment when they provide email
    if (email) {
      identify(undefined, () => {}, { name, zipCode }, email);

      // Post prospect when email is collected
      // This has been a noisy warning if this call fails, so adding a guard to eliminate this warning for most cases
      postProspect({
        tenant_id: userContext.tenantId,
        name: name || '',
        email,
        source: 'menu-modal',
        zip_code: zipCode,
      })
        .then((res) => res)
        .catch(async (err) => {
          // error code 1003 just indicates the user already exists so they can't be added as prospect;
          // we shouldn't log error in these cases.
          const errorCode = await err?.response?.error?.code;
          if (errorCode !== 1003) {
            logger.info({
              msg: 'Failed to post prospect',
              noticable: err,
            });
          }
        });
    }

    // If zip same as cart, check if we need to update email/name
    if (cart?.funnelData.zip === zipCode) {
      const needsUpdate =
        (email && cart?.funnelData.email !== email) ||
        (name && cart?.funnelData.name !== name);

      // Track if the menu modal was submitted
      track('Menu modal completed', {
        cartId: cart.uuid,
        email: email || cart.funnelData.email,
        recipientName: name || cart.funnelData.name,
        zip: zipCode,
        source: 'menu-modal',
        isAuthed: !!userContext?.user,
        modal_variant: getModalVariant(),
      });

      if (needsUpdate) {
        try {
          const { data } = await apolloClient.mutate<
            UpdateCartMutation,
            UpdateCartMutationVariables
          >({
            mutation: UPDATE_CART,
            context: { clientName: ClientNames.CART_ORDER_GRAPH },
            variables: {
              uuid: cart.uuid,
              funnelData: {
                ...(email && { email }),
                ...(name && { name }),
              },
              ...(name && {
                cartData: {
                  recipientName: name,
                },
              }),
            },
          });
          if (data?.updateCart?.__typename === 'Cart') {
            setCart(data.updateCart as FunnelCart);
          }
        } catch (err) {
          logger.error({
            msg: 'Failed to update cart with email/name',
            extra: { error: err },
          });
        }
      }

      setIsZipCodeModalOpen(false);
      return;
    }

    // Start background spinner while we're validating
    setIsLoading(true);

    const zipCodeValid = await validateZip({
      tenantId: userContext.tenantId,
      zip: zipCode,
    });

    if (!zipCodeValid) {
      setIsInvalidZipCode(true);
      setIsLoading(false);

      return;
    }

    track('Menu modal completed', {
      email: email || '',
      recipientName: name || '',
      zip: zipCode,
      source: 'menu-modal',
      isAuthed: !!userContext?.user,
      modal_variant: getModalVariant(),
    });

    // Store email/name for cart creation in useEffect
    if (email) setUserSuppliedEmail(email);
    if (name) setUserSuppliedName(name);
    setUserSuppliedZipCode(zipCode);
  };

  // Fiter useEffect
  useEffect(() => {
    setDisplayCatalogs(
      applyFilters({
        catalogs,
        filters: selectedFilters,
      }),
    );
  }, [selectedFilters]);

  // Update cart prefs
  useEffect(() => {
    if (cart) {
      setSellableCartPreferences({
        apolloClient,
        selectedFilters,
        cart,
      });
    }
  }, [selectedFilters]);

  // Sorter useEffect
  useEffect(() => {
    setDisplayCatalogs(
      displayCatalogs.map((catalog) => {
        return {
          ...catalog,
          items: sortCatalogItems({
            catalogItems: catalog.items,
            catalogSorters: selectedSorter.sorters,
          }),
        };
      }),
    );

    setCatalogs(
      catalogs.map((catalog) => {
        return {
          ...catalog,
          items: sortCatalogItems({
            catalogItems: catalog.items,
            catalogSorters: selectedSorter.sorters,
          }),
        };
      }),
    );
  }, [selectedSorter]);

  const totalNumMeals =
    cart?.cartDeliveries.reduce(
      (total, delivery) =>
        total +
        delivery.lineItems.reduce(
          (deliveryTotal, lineItem) => deliveryTotal + lineItem.quantity,
          0,
        ),
      0,
    ) ?? 0;

  // Get query param for menu entry version override (takes precedence over tenant config)
  // This determines which fields will be shown in the MenuEntryModal component
  const menuEntryOverride = queryParams.menuEntry as
    'ZIP' | 'EMAIL_ZIP' | 'FULL' | undefined;

  const getModalVariant = () =>
    menuEntryOverride ||
    tenantConfig.menuPage?.menuEntryModalConfig?.version ||
    'ZIP';

  return (
    <DiscountProvider discountCode={discount} totalNumMeals={totalNumMeals}>
      <MenuComponent
        cart={cart}
        setCart={setCart}
        catalogs={displayCatalogs}
        featuredSkuGroups={props.featuredSkuGroups}
        isLoading={isLoading}
        isZipCodeModalOpen={isZipCodeModalOpen}
        setIsZipCodeModalOpen={setIsZipCodeModalOpen}
        handleZipCodeModalSubmit={handleZipCodeModalSubmit}
        isInvalidZipCode={isInvalidZipCode}
        displayFilters={displayFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        selectedSorter={selectedSorter}
        setSelectedSorter={setSelectedSorter}
        menuEntryOverride={menuEntryOverride}
        selectedPlan={selectedPlan}
      />
    </DiscountProvider>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (
  context,
) => {
  const { authContext, tenant } = await getRequestContext(context.req);
  if (!tenant.config.featuredMealsSkus?.length) {
    return {
      props: {
        featuredSkuGroups: [],
      },
    };
  }

  const tenantId = authContext.tenantId;
  try {
    const featuredSkuGroupsResponse = await panaceaClient.get(
      '/v1/tenant-config/featured-sku-groups',
      {
        params: { tenantId },
      },
    );
    return {
      props: {
        featuredSkuGroups: featuredSkuGroupsResponse.data,
      },
    };
  } catch (err) {
    logger.error({
      msg: 'Error fetching featured sku groups on menu page. Page will load, but no meals will be featured.',
      noticable: err as Noticeable,
      extra: {
        context: {
          tenantId,
        },
      },
    });
    return {
      props: {
        featuredSkuGroups: [],
      },
    };
  }
};

export default Menu;
